import React from "react";
import { useQuery } from "@tanstack/react-query";
import { TrendingUp, Compass, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { api } from "@/lib/api";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { EmptyState } from "@/components/shared/EmptyState";

interface TrendsResp {
  total_reports: number;
  velocity_counts: Record<string, number>;
  top_themes: { theme: string; count: number }[];
  heatmap: { source: string; intensity: number }[];
}

const VEL_COLOR: Record<string, string> = {
  rising: "#7c3aed",
  emerging: "#2563eb",
  stable: "#64748b",
  saturated: "#f59e0b",
  declining: "#dc2626",
};

export const DiscoveryTrends: React.FC = () => {
  const trends = useQuery<TrendsResp>({
    queryKey: ["discovery-trends"],
    queryFn: async () => (await api.get("/discovery/trends")).data,
  });

  const t = trends.data;
  const total = t?.total_reports ?? 0;

  const themeData = (t?.top_themes || []).map((x) => ({
    name: x.theme.length > 18 ? x.theme.slice(0, 16) + "…" : x.theme,
    count: x.count,
  }));
  const heatData = (t?.heatmap || []).map((x) => ({
    name: x.source.charAt(0).toUpperCase() + x.source.slice(1),
    intensity: x.intensity,
  }));

  return (
    <DashboardLayout
      title="Discovery Trends"
      subtitle="Cross-report trend velocity and demand heatmap across your workspace."
      seoPath="/discovery/trends"
      seoDescription="Cross-report trend velocity and demand heatmap across your SociBaba Discovery workspace."
    >
      <Link
        to="/discovery"
        className="text-sm text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)] inline-flex items-center gap-1 mb-4"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Discovery
      </Link>

      {trends.isLoading ? (
        <div className="sg-skeleton h-96" />
      ) : total === 0 ? (
        <EmptyState
          title="No reports yet"
          description="Run at least one discovery search to see trend patterns emerge here."
          icon={<Compass className="h-5 w-5 text-[color:var(--sg-muted)]" />}
        />
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="velocity-cards">
            {(["rising", "emerging", "stable", "saturated", "declining"] as const).map((v) => (
              <div key={v} className="sg-card p-5" data-testid={`velocity-${v}`}>
                <div className="sg-label capitalize">{v}</div>
                <div className="sg-stat-num mt-3" style={{ color: VEL_COLOR[v] }}>
                  {t?.velocity_counts?.[v] ?? 0}
                </div>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="sg-card p-6" data-testid="top-themes-chart">
              <div className="sg-label mb-1">Top themes across reports</div>
              <div className="font-display text-lg font-semibold tracking-tight mb-4">
                What the market keeps talking about
              </div>
              <div className="h-72" style={{ minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%" minHeight={280}>
                  <BarChart data={themeData} layout="vertical" margin={{ top: 8, right: 8, left: 12, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                    <XAxis type="number" fontSize={11} tick={{ fill: "#64748b" }} />
                    <YAxis type="category" dataKey="name" fontSize={11} tick={{ fill: "#0f172a" }} width={120} />
                    <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="count" fill="#7c3aed" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="sg-card p-6" data-testid="heatmap-chart">
              <div className="sg-label mb-1">Source intensity heatmap</div>
              <div className="font-display text-lg font-semibold tracking-tight mb-4">
                Where signal density is strongest
              </div>
              <div className="h-72" style={{ minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%" minHeight={280}>
                  <BarChart data={heatData} margin={{ top: 8, right: 8, left: -8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" fontSize={11} tick={{ fill: "#64748b" }} />
                    <YAxis fontSize={11} tick={{ fill: "#64748b" }} />
                    <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="intensity" radius={[6, 6, 0, 0]}>
                      {heatData.map((_, i) => (
                        <Cell key={i} fill={["#7c3aed", "#2563eb", "#16a34a", "#f59e0b", "#dc2626", "#0f172a"][i % 6]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="sg-card p-6">
            <div className="sg-label mb-1">Read the trends</div>
            <div className="font-display text-lg font-semibold tracking-tight mb-3">
              How to interpret velocity
            </div>
            <div className="grid sm:grid-cols-5 gap-3 text-sm">
              <div><div className="font-semibold text-[color:var(--sg-violet)] mb-1">Rising</div><div className="text-[color:var(--sg-muted)] leading-relaxed">Fresh momentum — ideal window to move first.</div></div>
              <div><div className="font-semibold text-blue-700 mb-1">Emerging</div><div className="text-[color:var(--sg-muted)] leading-relaxed">Just breaking out. Educational content wins.</div></div>
              <div><div className="font-semibold text-slate-700 mb-1">Stable</div><div className="text-[color:var(--sg-muted)] leading-relaxed">Steady demand — depend on positioning to win.</div></div>
              <div><div className="font-semibold text-amber-700 mb-1">Saturated</div><div className="text-[color:var(--sg-muted)] leading-relaxed">Crowded market. Differentiate hard or skip.</div></div>
              <div><div className="font-semibold text-rose-700 mb-1">Declining</div><div className="text-[color:var(--sg-muted)] leading-relaxed">Interest waning — pivot or find sub-niches.</div></div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default DiscoveryTrends;
